import { Stack, Typography } from '@mui/material';
import { first, isEmpty } from 'lodash';
import { ReactNode } from 'react';

import { AlertCircleIcon } from '@/components/icons/AlertCircleIcon';
import { SlideStackIcon } from '@/components/icons/SlideStackIcon';
import { PresentationBundleKind, PresentationPageKind } from '@/types/schema';
import { getPublicImageUrl } from '@/utils/staticFiles';

import { BORDER_COLOR_WARNING } from '../../../PresentationTemplateEditor.constants';
import {
  PresentationTemplateEditorBundle,
  PresentationTemplateEditorPage,
} from '../../../PresentationTemplateEditor.types';
import { isDocumentSummaryConfigIncomplete } from '../documentSummaryBundleConfig/DocumentSummaryBundleConfigModal.utils';
import { isEntitySummaryConfigIncomplete } from '../entitySummaryBundleConfig/EntitySummaryBundleConfigModal.utils';
import {
  getTemplateBundleDisplayName,
  getTemplateBundleLabel,
  getTemplateBundleLabelPageInput,
  getTemplateBundleSubLabel,
} from '../PresentationTemplateStageEditor.utils';

function getBundleLabelInput(bundle: PresentationTemplateEditorBundle) {
  const firstPage = first(bundle.pages);
  return {
    kind: bundle.kind,
    ...getTemplateBundleLabelPageInput(firstPage),
    dividerTemplateBundleConfiguration:
      bundle.dividerTemplateBundleConfiguration,
    documentSummaryTemplateBundleConfiguration:
      bundle.documentSummaryTemplateBundleConfiguration,
  };
}

export function getBundleTileLabel(
  bundle: PresentationTemplateEditorBundle
): string {
  return getTemplateBundleLabel(getBundleLabelInput(bundle));
}

export interface BundleTileLabels {
  label: string;
  /** User-entered title, shown beneath the label. Null when there isn't one. */
  subLabel: string | null;
}

/**
 * Returns the tile's label and optional subtext. Bundles that carry a
 * user-entered title (cover, divider, custom) show the bundle type as the
 * label and the title as subtext, so several of the same kind can be told
 * apart at a glance. Everything else keeps a single label.
 */
export function getBundleTileLabels(
  bundle: PresentationTemplateEditorBundle
): BundleTileLabels {
  const input = getBundleLabelInput(bundle);
  const subLabel = getTemplateBundleSubLabel(input);

  if (!subLabel) {
    return { label: getTemplateBundleLabel(input), subLabel: null };
  }

  const label = getTemplateBundleDisplayName(input.kind, input.firstPageKind);
  // A title matching its own bundle type would render the same text twice.
  return { label, subLabel: subLabel === label ? null : subLabel };
}

const BUNDLE_TO_ICON_MAP: Partial<
  Record<PresentationBundleKind | PresentationPageKind, ReactNode>
> = {};

const DEFAULT_ICON = (
  <img
    src={getPublicImageUrl('/slideThumbnail.svg')}
    alt="Slide thumbnail"
    style={{ padding: '12px 0', width: '100%' }}
  />
);

const MISSING_DATA_ICON = (
  <Stack
    direction="column"
    alignItems="center"
    justifyContent="center"
    width="100%"
    height="100%"
    spacing={1}
  >
    <AlertCircleIcon
      size={64}
      color={BORDER_COLOR_WARNING}
      sx={{ '& path': { strokeWidth: 1 } }}
    />
    <Typography variant="body-xs" sx={{ userSelect: 'none' }}>
      Review and edit
    </Typography>
  </Stack>
);

export function getBundleTileIcon(
  bundle: PresentationTemplateEditorBundle
): ReactNode {
  if (getBundleTileHasMoreInformationRequired(bundle)) {
    return MISSING_DATA_ICON;
  }

  return BUNDLE_TO_ICON_MAP[bundle.kind] || DEFAULT_ICON;
}

/** Returns the default icon for a bundle without checking for missing data. */
export function getBundleTileDefaultIcon(
  bundle: PresentationTemplateEditorBundle
): ReactNode {
  return BUNDLE_TO_ICON_MAP[bundle.kind] || DEFAULT_ICON;
}

/**
 * Bundle kinds whose output contains multiple pages, indicated by a stack
 * icon next to the tile label.
 */
const MULTI_PAGE_BUNDLE_ICON_MAP: Partial<
  Record<PresentationBundleKind, React.ComponentType<{ size?: number }>>
> = {
  [PresentationBundleKind.WaterfallOverviewBundle]: SlideStackIcon,
  [PresentationBundleKind.BeneficiaryReportBundle]: SlideStackIcon,
  [PresentationBundleKind.WaterfallComparisonBundle]: SlideStackIcon,
  [PresentationBundleKind.EstateInsightsBundle]: SlideStackIcon,
  [PresentationBundleKind.EntitySummaryBundle]: SlideStackIcon,
};

/**
 * Returns a multi-page icon for bundles that produce multiple pages in
 * their output, or null for single-page bundles.
 * The icon inherits the parent's text color via currentColor.
 */
export function getBundleTileMultiPageIcon(
  bundle: PresentationTemplateEditorBundle
): ReactNode {
  const IconComponent = MULTI_PAGE_BUNDLE_ICON_MAP[bundle.kind];
  if (!IconComponent) return null;
  return <IconComponent size={16} />;
}

/** Bundles that are configured via the page editor stage. */
const PAGE_EDITOR_BUNDLES: PresentationBundleKind[] = [
  PresentationBundleKind.CoverSlideBundle,
  PresentationBundleKind.CustomPageBundle,
  PresentationBundleKind.DividerBundle,
];

/** Bundles that are configured via a modal instead of the page editor stage. */
const MODAL_CONFIGURABLE_BUNDLES: PresentationBundleKind[] = [
  PresentationBundleKind.WaterfallOverviewBundle,
  PresentationBundleKind.BeneficiaryReportBundle,
  PresentationBundleKind.WaterfallComparisonBundle,
  PresentationBundleKind.DocumentSummaryBundle,
  PresentationBundleKind.EstateInsightsBundle,
  PresentationBundleKind.EntitySummaryBundle,
];

/**
 * Whether the bundle uses the page editor stage specifically.
 * Used by the slide editor sidebar to filter which bundles appear.
 */
export function isBundlePageEditable(
  bundle: PresentationTemplateEditorBundle
): boolean {
  return PAGE_EDITOR_BUNDLES.includes(bundle.kind);
}

export function getBundleTileHasMoreInformationRequired(
  bundle: PresentationTemplateEditorBundle
): boolean {
  if (
    !PAGE_EDITOR_BUNDLES.includes(bundle.kind) &&
    !MODAL_CONFIGURABLE_BUNDLES.includes(bundle.kind)
  ) {
    return false;
  }

  if (bundle.kind === PresentationBundleKind.DividerBundle) {
    return isEmpty(bundle.dividerTemplateBundleConfiguration?.dividerTitle);
  }

  if (bundle.kind === PresentationBundleKind.WaterfallOverviewBundle) {
    const config = bundle.waterfallTemplateBundleConfiguration;
    if (!config) return true;
    // At least one page must be selected
    return !(
      config.includeCoverPage ||
      config.includeAssumptionsPage ||
      config.includeSummaryPage ||
      config.includeDiagramPage ||
      config.includeTaxOverviewPage
    );
  }

  if (bundle.kind === PresentationBundleKind.BeneficiaryReportBundle) {
    const config = bundle.beneficiariesTemplateBundleConfiguration;
    if (!config) return true;
    // At least one page must be selected
    return !(
      config.includeHouseholdOverview || config.includeIndividualOverviews
    );
  }

  if (bundle.kind === PresentationBundleKind.WaterfallComparisonBundle) {
    // The comparison page is always included, so config existence is sufficient
    return !bundle.waterfallComparisonTemplateBundleConfiguration;
  }

  if (bundle.kind === PresentationBundleKind.EstateInsightsBundle) {
    const config = bundle.insightsTemplateBundleConfiguration;
    if (!config) return true;
    return !(
      config.includeActiveInsights ||
      config.includeMissingDocuments ||
      config.includeCompletedInsights
    );
  }

  if (bundle.kind === PresentationBundleKind.DocumentSummaryBundle) {
    return isDocumentSummaryConfigIncomplete(
      bundle.documentSummaryTemplateBundleConfiguration
    );
  }

  // Must be handled before the page fallback below: this bundle intentionally
  // carries no pages, so the fallback would report it as complete.
  if (bundle.kind === PresentationBundleKind.EntitySummaryBundle) {
    return isEntitySummaryConfigIncomplete(
      bundle.entitySummaryTemplateBundleConfiguration
    );
  }

  return bundle.pages.some(pageNeedsMoreInformation);
}

function pageNeedsMoreInformation(
  page: PresentationTemplateEditorPage
): boolean {
  switch (page.kind) {
    case PresentationPageKind.CustomPage: {
      const { customPage } = page;
      if (!customPage) return true;

      // Header is always required
      if (isEmpty(customPage.pageName)) return true;

      // Content is satisfied by either pageContent or a customPrompt
      // (the prompt will be used to generate content)
      if (isEmpty(customPage.pageContent) && isEmpty(customPage.customPrompt))
        return true;

      return false;
    }
    case PresentationPageKind.CoverPage: {
      // The cover slide title is optional: a blank title shows a
      // placeholder in the designer and renders nothing in print, so the
      // cover page never needs more information. [T1-6300]
      return false;
    }
    default:
      return false;
  }
}
